import React, { useState } from 'react';

import {
  Drawer,
  DrawerHeader,
  DrawerBody,
  Box,
  Badge,
  Button,
  UserIcon,
  Text,
  Link,
  ArrowRightIcon,
  EditIcon,
  useToast,
  Spinner,
} from '@razorpay/blade/components';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { salaryStructureQueries, warningModalTypes } from './constants';
import api from 'api';
import SalaryStructureComponentDetails from './components/SalaryStructureComponentDetails';

import { SalaryStructureWarningModals } from './creation/components/SalaryStructureWarningModals';
import { useFormWithProvider } from 'forms';
import { SalaryStructureFormData } from './creation';
import { parseGetSalaryStructureResponse } from 'components/SalaryStructure/utils';
import { AppError } from 'utils/AppError';
import { ToggleSalaryStructureModal } from './creation/components/ToggleSalaryStructureModal';
import { getToggleModalContent } from './utils';
import { GetSalaryStructureResponse } from 'components/SalaryStructure/types';
import { SalaryStructureEmployeeDetailsModal } from './creation/components/SalaryStructureEmployeeDetailsModal';
import { SalaryStructuresErrorStateView } from './creation/components/SalaryStructureErrorStateView';

function SalaryStructureDetails({
  isDrawerOpen,
  closeDrawer,
  structureId,
  onEdit,
}: {
  isDrawerOpen: boolean;
  closeDrawer: () => void;
  structureId: string | null;
  onEdit: () => void;
}) {
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const [showToggleModal, setShowToggleModal] = useState(false);
  const [showEmployeeDetails, setShowEmployeeDetails] = useState(false);

  const { formData, setFormData } = useFormWithProvider<SalaryStructureFormData>();

  const queryClient = useQueryClient();
  const toast = useToast();

  const setSalaryStructureStatusMutation = useMutation({
    mutationKey: [salaryStructureQueries.setSalaryStructureStatus],
    mutationFn: api.salaryStructures.mutations.setSalaryStructureStatus,
    onSuccess: () => {
      toast.show({
        content: 'Salary structure created successfully',
        color: 'positive',
      });

      queryClient.invalidateQueries([salaryStructureQueries.getSalaryStructuresList]);

      queryClient.refetchQueries({
        queryKey: [salaryStructureQueries.getSalaryStructuresList],
      });

      closeDrawer();
    },
    onError: (e: AppError) => {
      toast.show({
        content: e.message,
        color: 'negative',
      });
    },
  });

  const { data, isFetching, isLoading, error, refetch } = useQuery({
    queryKey: [salaryStructureQueries.getSalaryStructure, structureId],
    queryFn: async () => {
      const result = await api.salaryStructures.queries.getSalaryStructure({
        input: {
          id: structureId || '',
        },
      });

      const response = parseGetSalaryStructureResponse(result);
      return response.payrollGetSalaryStructure.data;
    },
    onSuccess: (data) => {
      setFormData({ ...formData, templateId: [data.id] });
    },
    enabled: !!structureId,
  });

  const getBadge = (status: string) => {
    if (status == 'SALARY_STRUCTURE_STATUS_ACTIVE') {
      return <Badge color="positive">Active</Badge>;
    }
    return <Badge color="notice">Disabled</Badge>;
  };

  const isSalaryStructureEnabled = data?.status === 'SALARY_STRUCTURE_STATUS_ACTIVE';
  const toggleSalaryStructureStatus = () => {
    closeDrawer();
    setShowToggleModal(true);
  };

  const submitToggleSalaryStructure = () => {
    const status =
      data?.status === 'SALARY_STRUCTURE_STATUS_ACTIVE'
        ? 'SALARY_STRUCTURE_STATUS_INACTIVE'
        : 'SALARY_STRUCTURE_STATUS_ACTIVE';
    setSalaryStructureStatusMutation.mutate({
      input: {
        updates: [{ salaryStructureId: data?.id as string, status: status }],
      },
    });
  };

  const handleEditStructureClick = () => {
    closeDrawer();
    setFormData({
      ...formData,
      templateId: [data?.id as string],
      isEditTemplateMode: true,
    });
    setIsWarningModalOpen(true);
  };

  const { title, description } = getToggleModalContent(data as GetSalaryStructureResponse);

  const isError = Boolean(error);
  const isLoadingOrErrorState = isFetching || isLoading || isError;

  const renderLoadingOrErrorState = () => (
    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
      {isError ? (
        <SalaryStructuresErrorStateView
          title=""
          description="There was an error is fetching the component details."
          retryText={'Retry'}
          onRetry={refetch}
        />
      ) : (
        <Spinner accessibilityLabel="loading salary structure" />
      )}
    </Box>
  );

  const renderEmployeeInfoCard = () => (
    <Box
      display="flex"
      paddingY="spacing.7"
      paddingX="spacing.4"
      gap="spacing.4"
      borderColor={'surface.border.gray.muted'}
      borderWidth={'thin'}
      borderRadius={'medium'}
      alignItems={'center'}>
      <Box
        backgroundColor="surface.background.sea.subtle"
        width="spacing.8"
        height="spacing.8"
        borderRadius={'round'}
        display={'flex'}
        alignItems={'center'}
        justifyContent={'center'}>
        <UserIcon size="medium" color="feedback.icon.positive.intense" />
      </Box>
      <Box display="flex" flexDirection="column" gap="spacing.2" justifyContent={'center'}>
        <Text weight="regular" size="medium" color="surface.text.gray.subtle">
          {!data?.employeeCount
            ? 'No employee assigned'
            : `Salary structure declared by ${data?.employeeCount} employees`}
        </Text>

        {!!data?.employeeCount && (
          <Link
            variant="button"
            iconPosition="right"
            icon={ArrowRightIcon}
            onClick={() => {
              setShowEmployeeDetails(true);
              closeDrawer();
            }}>
            View Details
          </Link>
        )}
      </Box>
    </Box>
  );

  const renderActionButtons = () => (
    <Box
      display="flex"
      gap="spacing.4"
      alignItems={'center'}
      position={'fixed'}
      bottom={'0%'}
      height={'10%'}
      backgroundColor={'surface.background.gray.intense'}
      width={`calc(100% - 40px)`}>
      <Button
        variant="primary"
        icon={EditIcon}
        iconPosition="left"
        onClick={handleEditStructureClick}
        isFullWidth
        isDisabled={isFetching || !data || !!error}
        isLoading={isFetching}>
        Edit Structure
      </Button>

      {data?.type !== 'SALARY_STRUCTURE_TYPE_DEFAULT' && (
        <Button
          variant="secondary"
          color={isSalaryStructureEnabled ? 'negative' : 'positive'}
          onClick={toggleSalaryStructureStatus}
          isDisabled={isFetching || !data || !!error}
          isLoading={isFetching}>
          {isSalaryStructureEnabled ? 'Disable' : 'Enable'}
        </Button>
      )}
    </Box>
  );

  const renderMainContent = () => (
    <Box gap="spacing.6" display="flex" flexDirection="column" position="relative" height={'100%'}>
      <Box gap="spacing.6" display="flex" flexDirection="column" overflow={'auto'} height={'85%'}>
        {renderEmployeeInfoCard()}
        <SalaryStructureComponentDetails salaryComponents={data?.salaryStructureComponents} />
      </Box>
      {renderActionButtons()}
    </Box>
  );

  return (
    <>
      {isWarningModalOpen && (
        <SalaryStructureWarningModals
          isOpen={isWarningModalOpen}
          onClose={() => setIsWarningModalOpen(false)}
          primaryButtonText="Proceed to edit"
          isSecondaryButton={true}
          secondaryButtonText="Cancel"
          onSecondaryButtonClick={() => setIsWarningModalOpen(false)}
          onPrimaryButtonClick={() => {
            setIsWarningModalOpen(false);
            closeDrawer();
            onEdit();
          }}
          type={warningModalTypes.PROCEED_TO_EDIT}
        />
      )}

      {showToggleModal && (
        <ToggleSalaryStructureModal
          isOpen={showToggleModal}
          onClose={() => setShowToggleModal(false)}
          title={title}
          subtitle={'What will happen?'}
          description={description}
          isActive={data?.status === 'SALARY_STRUCTURE_STATUS_ACTIVE'}
          onPrimaryButtonClick={submitToggleSalaryStructure}
          onSecondaryButtonClick={() => setShowToggleModal(false)}
          isLoading={setSalaryStructureStatusMutation.isLoading}
        />
      )}

      {showEmployeeDetails && (
        <SalaryStructureEmployeeDetailsModal
          isOpen={showEmployeeDetails}
          onDismiss={() => setShowEmployeeDetails(false)}
          salaryStructureData={data}
        />
      )}
      <Drawer isOpen={isDrawerOpen} onDismiss={closeDrawer}>
        {!isLoadingOrErrorState && (
          <DrawerHeader
            title={data?.name}
            titleSuffix={getBadge(data?.status as string)}
            subtitle={data?.description}
          />
        )}
        <DrawerBody>
          {isLoadingOrErrorState ? renderLoadingOrErrorState() : renderMainContent()}
        </DrawerBody>
      </Drawer>
    </>
  );
}

export { SalaryStructureDetails };
